<template>

	<div :class="{'project-check modal':true, 'data-dive-limit-reached': dataDiveLimitReached, 'no-default': true}">

		<div class="modal-wrap">

			<div class="modal-inner">

				<h2 class="modal-title">{{ title }}<span v-if="!projectCreated&&totalSteps>1"> - Step {{step}} of {{totalSteps}}</span><span class="close" @click="close">X</span></h2>

				<Loading v-if="loading!==''" position="inner" :text="loading" />

				<div class="modal-inner-scroll" v-if="!projectCreated">

					<div class="no-subscription" v-if="['init','tracking-location','keywords-to-track'].indexOf(currentStep)>-1&&needsActiveSubscription">You currently don't have an active subscription so your rank tracking keywords will not be checked, when you've finished here please head over to the <router-link target='_blank' to="/account/subscriptions">subscriptions page</router-link>.<span class="close" @click="currentSubscription=true">X</span></div>

					<div class="no-subscription" v-if="dataDiveLimitReached">
						Sorry you have hit your Data Dive domain limit for your current plan. Please head over to the <router-link to="/account/subscriptions">subscriptions page</router-link> to upgrade.
					</div>

					<form :class="{form:true,'small-form':!hasProjectSelection}" @submit.prevent="" v-if="currentStep==='init'&&!dataDiveLimitReached">

						<h2 class="main-heading" v-html="openingTitle" />

						<div :class="{'input-2-row': hasProjectSelection, 'input-1-row':!hasProjectSelection}">

							<div class="setup-input-wrap" v-if="project.enabled.rankTracking">

								<FormInput 
									type="text" 
									v-model="project.name"  
									name="name" 
									label="Project Name"
									tip="Enter the name of the project you are creating"
									:noMarginBottom="true"
									:required="false"
								/>

							</div>

							<div class="setup-input-wrap">

								<FormInput 
									type="url" 
									v-model="project.url"  
									name="url" 
									:label="!project.enabled.dataDive?'Project Domain':'Domain'"
									placeholder="Including https://"
									:tip="!isCopyingProject?'Enter the URL including https://':''"
									@has-internal-errors="hasInternalErrors"
									:noMarginBottom="true"
									:required="false"
									:disabled="isCopyingProject"
								/>

								<p class="url-error" v-if="urlError" v-html="urlError" />

							</div>

							<div class="setup-input-wrap" v-if="project.enabled.dataDive">

								<label>Please select the language your site is in.</label>
								<v-select 
									placeholder="Language"
									v-model="project.languageCode" 
									:options="languages" 
									:reduce="option => option._id"  
									:append-to-body="true" 
									:clearable="false"
								/>

							</div>

						</div>

						<div v-if="hasProjectSelection">

							<hr />

							<h2 class="main-heading">What Do You Want To Use This Project For?</h2>
							<p class="p-info">Choose one or more of the following options (you can add more after the project has been created)</p>

							<div class="project-types">

								<div 
									v-for="(projectType, projectTypeIndex) in projectTypes" 
									:key="'project-type-'+projectTypeIndex"   
									:class="{'project-type': true, selected: project.enabled[projectType.name], 'not-available': !!projectType.notAllowed }"
									@click="selectProjectType(projectType)"
								>

									<div class="header-area">

										<svg xmlns="http://www.w3.org/2000/svg" :viewBox="svg[projectType.name].viewBox" v-html="svg[projectType.name].paths" />

										<div class="h-wrap">

											<h2 class="project-type-title" v-html="projectType.title" />

										</div>

									</div>

									<span class="added-to-project" v-if="project.enabled[projectType.name]&&!projectType.notAllowed" >Added to project <TickCross :enabled="true" :hideCross="true" :pulse="false" /></span>

									<span class="added-to-project" v-if="!project.enabled[projectType.name]&&!projectType.notAllowed">Click to add</span>
									<p>
										<span v-html="projectType.description" /><br v-if="projectType.video">
										<span v-if="projectType.video" @click="showVideo($event,projectType.video)" class="video-link">Need more info? Try this video</span>
									</p>
									<p class="error" v-if="projectType.notAllowed"><span v-html="projectType.notAllowed"></span></p>
									<div class="project-type-overlay"></div>
								</div>

							</div>

						</div>

					</form>

					<form :class="{form:true}" @submit.prevent="" v-if="currentStep==='tracking-location'">

						<h1 class="big-heading"><Icon type="rank-tracking" class="svg" /><span>Search, Location &amp; Language</span></h1> 

						<div class="tracking-types">

							<div class="search-device-types">

								<h2 class="main-heading">Search &amp; Device Type</h2>

								<p class="info" v-if="!isEditingProject">Select your search type and 1 or both device types, selecting both will create 2 projects.</p>

								<p class="info" v-if="isEditingProject">Select your search type and device type.</p>

								<MultiTypes 
									title="Search Type (select 1)"
									:current="project.searchTypes"
									:multiTypes="multiTypes.search"
									type="searchTypes"
									@clicked="multiTypeClicked"
									:single="true"
									:pulse="pulseSearchTypes"
								/>

								<MultiTypes 
									title="Device Types (select 1 or both)"
									:current="project.devices"
									:multiTypes="multiTypes.devices"
									type="devices"
									:small="true"
									:pulse="pulseDeviceTypes"
									@clicked="multiTypeClicked"
								/>

							</div>

							<div class="location-info">

								<p v-if="creatingMultipleProjects.count" class="creating-multiple">
									<span>Your selections will create {{creatingMultipleProjects.count}} projects named...</span><br /> 
									{{creatingMultipleProjects.text}} (you can rename these once they have been created)
								</p>

								<hr />

								<div class="select-wraps">

									<div class="select-wrap">

										<h2 class="main-heading">Language</h2>

										<p class="info">
											Select the language you'd like to search in.
										</p>

										<v-select 
											placeholder="Language"
											v-model="project.languageCode" 
											:options="languages" 
											:reduce="option => option._id"  
											:append-to-body="true" 
											:clearable="false"
										></v-select>

									</div>

									<div class="select-wrap">

										<h2 class="main-heading">Location</h2>

										<p class="info">
											Select the country you'd like to search in.
										</p>

										<v-select 
											placeholder="Country"
											v-model="project.locationCode" 
											:options="locations" 
											:reduce="option => option._id" 
											:disabled="!showAddressSearch" 
											:clearable="false"
										></v-select>

									</div>

								</div>

								<p class="info" v-if="project.searchTypes.length>0&&project.searchTypes.indexOf('local')<0&&project.locationCode">
									<span @click="showCityStreet=!showCityStreet" class="edit">Click here</span> <span v-html="!showCityStreet?'if you\'d like to narrow the search to a street or city level':'if you don\'t need to narrow the search to a street or city level'" />.
								</p>

								<p class="info" v-if="project.searchTypes.length>0&&project.searchTypes.indexOf('local')<0&&project.locationCode&&showCityStreet">
									Street or city level?
								</p>

								<p class="info" v-if="project.searchTypes.length>0&&project.searchTypes.indexOf('local')>=0&&project.locationCode">
									Should we do your local search at a street or city level? <span v-if="creatingMultipleProjects.count&&project.searchTypes.indexOf('organic')>=0" class="red">*This will be applied to both the Organic and Local projects, if this is not what you require please create the projects separately.*</span>
								</p>

								<MultiTypes 
									:current="level"
									:multiTypes="multiTypes.map"
									:small="true"
									type="map"
									@clicked="multiTypeClicked"
									v-if="project.searchTypes.length>0&&project.locationCode&&(project.searchTypes.indexOf('local')>=0||showCityStreet)"
								/>

								<p class="info" v-if="level==='city'&&!project.locationName">
									Please search for your city below
								</p>

								<p class="info" v-if="level==='street'&&!project.locationName">
									Please search for your street below
								</p>

								<vue-google-autocomplete 
									v-if="project.locationCode && showAddressSearch && theLevel === 'city'"
									id="mapCity"
									v-on:placechanged="getAddressData"
									:types="'(regions)'"
									:placeholder="'Start typing your City/Town/Area'"
									:country="country"
									:key="'mapCity'+autoCompleteKey"
								/>

								<vue-google-autocomplete 
									v-if="project.locationCode && showAddressSearch && theLevel === 'street'"
									id="mapStreet"
									v-on:placechanged="getAddressData"
									:types="'address'"
									:placeholder="'Start typing your address'"
									:country="country"
									:key="'mapStreet'+autoCompleteKey"
								/>

								<div class="location-name" v-if="project.locationName">

									<span>Set to {{ project.locationName }}</span>

									<span class="edit" @click="changeCity">Remove/Edit</span>

								</div>

								<p class="info" v-if="project.searchTypes.indexOf('local')>=0&&project.locationCoordinate">
									You can click the map to drop the pin exactly where you'd like the search to be taken, we will also use the same zoom level you select.
								</p>

								<div class="setup-input-wrap-large map-wrap">

									<googlemaps-map 
										ref="mapRef"
										:center.sync="map.center"
										:zoom.sync="project.locationZoom"
										:options="map.options"
										@idle="map => mapBounds = map.getBounds()"
										@click="setMapLocation" 
										v-if="project.searchTypes.indexOf('local')>=0&&project.locationCoordinate"
										style="height:300px;"
									>
										<googlemaps-marker 
											:draggable="true"
											:position="map.center" 
										/>
									</googlemaps-map>

								</div>

							</div>

						</div>

					</form>

					<form :class="{form:true}" @submit.prevent="" v-if="currentStep==='keywords-to-track'">

						<h1 class="big-heading"><Icon type="rank-tracking" class="svg" /><span>Add Some Keywords</span></h1> 

						<p class="info" v-if="!isCopyingProject">
						<strong>Type/paste</strong> them in and use the <strong>ideas</strong> we've found for you --OR-- <strong>Upload a CSV</strong> (historical data from another tool or just a list of keywords).</p>

						<p class="info bold" v-if="!!isCopyingProject"><strong>All keywords will be copied over from {{copyFrom.name}} automatically, you only need to add additional keywords below if you want to.</strong></p>

						<div class="keyword-adder-wrap">

							<MultiTypes 
								:current="keywordEntryType"
								:multiTypes="multiTypes.keywords"
								:showTickCross="false"
								type="keywords"
								@clicked="multiTypeClicked"
								class="keyword-adder-types"
								v-if="!isCopyingProject"
							/>

							<div :class="{'keyword-adder-inner-wrap':true,[keywordEntryType]: true}">

								<div v-if="creatingMultipleProjects.count<2&&!isCopyingProject&&keywordEntryType==='upload'" class="video-message">
									If you are unsure of how the CSV should be setup checkout the <span @click="showVideo($event,'rank-tracking-csv-import')">video here</span>.
								</div>

								<Keywords
									:noModal="true"
									:showProjects="false"
									:emitChanges="true"
									:csvUpload="!isCopyingProject&&keywordEntryType==='upload'"
									:disableCSVupload="creatingMultipleProjects.count>1"
									:showKeywordInput="isCopyingProject||keywordEntryType==='input'"
									:showCSVhint="false"
									:frequencyLabelOverride="frequencyLabelOverride"
									:keywordsLabelOverride="keywordsLabelOverride"
									:key="keywordsKey"
									:keywords="project.keywords"
									:project="isEditingProject?projectInfo._id:''"
									:required="false"
									@changed="keywordsChanged"
									v-if="!!keywordEntryType||isCopyingProject"
									class="keyword-adder"
									:focus="true"
									:autoResizeMaxHeight="250"
								/>

								<div 
									v-if="keywordEntryType==='input'" 
									:class="{'keyword-suggestions': true, loading: keywordIdeasChecking}"
								>
									<label v-if="!keywordIdeas.length">Keywords Ideas</label>
									<label v-else>We've found {{keywordIdeas.length}} keyword ideas for you...</label>
									<p class="info" v-if="keywordIdeasChecked&&keywordIdeas.length">Click any you'd like to track<span v-if="keywordsNew">, we've added the top 5</span>.</p>
									<p class="info" v-if="keywordIdeasChecked&&!keywordIdeas.length">Sorry there are no keyword ideas for you at the moment, please add your own.</p>
									<div class="keyword-suggestions-list-wrapper" v-if="keywordIdeasChecked&&keywordIdeas.length">
										<h5 v-if="keywordIdeas.length">Competition</h5>
										<div class="keyword-suggestions-list">
											<div v-for="(idea, index) in keywordIdeas" 
												:key="'keyword-idea-'+index"
												:class="{'keyword-suggestion': true, 'selected': selectedKeywords.includes(idea.keyword)}"
												@click="toggleKeyword(idea.keyword)">
												<div class="keyword-info">
													<span class="keyword">{{idea.keyword}}</span>
													<span class="volume" v-if="idea.avgMonthlySearches">{{idea.avgMonthlySearches}} searches/mo</span>
												</div>
												<div class="competition" v-if="idea.competition" :class="idea.competition.toLowerCase()" :title="idea.competition+' (Competition Index)'">
													{{idea.competition}}
												</div>
											</div>
										</div>
									</div>
									<Loading v-if="keywordIdeasChecking" position="inner" text="Loading keyword ideas (this can take upto 30 seconds)..." />
								</div>

								<div v-if="creatingMultipleProjects.count>1&&!isCopyingProject&&keywordEntryType==='upload'" class="csv-upload-disabled">
									You can only upload a CSV when creating a single project.
								</div>

							</div>

						</div>

					</form>

					<form :class="{form:true}" @submit.prevent="" v-if="currentStep==='email-insights'">

						<h1 class="big-heading"><Icon type="insights" class="svg" /><span>Email Insights</span></h1> 

						<p class="info" v-if="project.enabled.rankTracking">Activate Email Insights for weekly updates that spotlight key opportunities and insights for your tracked keywords.</p>
						<p class="info" v-if="project.enabled.dataDive">Activate Email Insights for weekly updates that spotlight key opportunities and insights based on your google search console &amp; analytics data.</p>

						<p class="info msg" v-if="showInsightsMessage&&!insightsMessageShown"><em>Are you sure you don't want to turn on Email Insights?</em> <br>You might be overlooking vital opportunities and insights that could boost your project's performance (click <em>{{proceedLabel}}</em> again to go ahead without it.).</p>

						<div class="alerts-wrap">

							<div 
								:class="{alert:true }"
							>

								<h3 class="alert-name" v-if="project.enabled.rankTracking">Do you want to receive Email Insights for this Rank Tracking project?</h3>
								<h3 class="alert-name" v-if="project.enabled.dataDive">Do you want to receive Email Insights for this domain?</h3>

								<Switcher 
									:sameColour="true"  
									v-model="project.emailInsights" 
									:title="!!project.emailInsights?'Yes':'No'"
									:styles="'margin: 0;'"
								/>

							</div>

						</div>

					</form>

					<form :class="{form:true}" @submit.prevent="" v-if="currentStep==='alerts'">

						<h1 class="big-heading"><Icon type="alerts" class="svg" /><span>Rank Tracking - Alerts</span></h1> 

						<p class="info" v-html="alertsDescription" />

						<div class="alerts-wrap">

							<div 
								v-for="(alert, alertIndex) in alerts" 
								:key="'alert-'+alertIndex"   
								:class="{alert:true }"
							>

								<h3 class="alert-name" v-html="alert.name" />

								<Switcher 
									:sameColour="true"  
									v-model="includeAlerts[alert._id]" 
									:title="!!includeAlerts[alert._id]?'Turn on':'Turn off'"
									:styles="'margin: 0;'"
								/>

							</div>

						</div>

					</form>

					<form :class="{form:true,hidden:currentStep!=='google'}" @submit.prevent="">

						<h1 class="big-heading"><Icon type="data-dive" class="svg" /><span>Data Dive</span></h1> 

						<p v-if="copyingAndHasGSC">
							You are copying a project that has Google Search Console connected, you can't connect another one.
						</p>

						<p v-if="!copyingAndHasGSC&&hasDataDiveAlready" class="red error">
							You already have Data Dive connected to this domain.
						</p>

						<GoogleConnect 
							:domain="domain"
							scope="https://www.googleapis.com/auth/webmasters.readonly https://www.googleapis.com/auth/analytics.readonly https://www.googleapis.com/auth/userinfo.email"
							clientId="444580885070-gc0jr5mklt4keng77gum1saqme2isr9c.apps.googleusercontent.com"
							@complete="googleConnectComplete"
							@loading="googleLoadingChange"
							v-if="!copyingAndHasGSC&&!hasDataDiveAlready"
						/>

					</form>

					<form :class="{form:true}" @submit.prevent="" v-if="currentStep==='content-keywords'">

						<h1 class="big-heading"><Icon type="content-keywords" class="svg" /><span>Content &amp; Keywords</span></h1> 

						<div :class="{'content-keywords-wrap': true,split:(!project.enabled.rankTracking||!project.locationCode)&&(!hideLocation||!hideLanguage)}" v-if="allowed.contentKeywords">

							<div>

								<p class="info">Enter 1-3 keywords/topics related to your website or main categories to get started (any more than 3 will be ignored, you can add more in the content &amp; keywords area later).</p>

								<FormInput 
									type="textarea" 
									v-model="project.contentKeywords"
									name="content-keywords" 
									placeholder="One per line..."
									theRef="content-keywords"
									:autoResize="true"
									:autoResizeMaxHeight="300"
									:heightBuffer="25"
									:noMarginBottom="true"
									:focus="true"
								/>

							</div>

							<div>

								<div class="location" v-if="((!project.enabled.rankTracking&&!project.enabled.dataDive)||!project.locationCode)&&!hideLocation">

									<h2 class="main-heading">Location</h2>

									<p class="info">
										Please select the country you'd like your keyword ideas from.
									</p>

									<v-select 
										placeholder="Location"
										v-model="project.locationCode" 
										:options="locations" 
										:reduce="option => option._id" 
									></v-select>

								</div>

								<div class="language" v-if="((!project.enabled.rankTracking&&!project.enabled.dataDive)||!project.languageCode)&&!hideLanguage">

									<h2 class="main-heading">Language</h2>

									<p class="info">
										Please select the language you'd like your keyword ideas in.
									</p>

									<v-select 
										placeholder="Language (ignore to use default)"
										v-model="project.languageCode" 
										:options="languages" 
										:reduce="option => option._id"  
										:append-to-body="true"
									></v-select>

								</div>

							</div>

						</div>

						<div :class="{'content-keywords-wrap': true}" v-if="!allowed.contentKeywords">

							<div>

								<p class="info red">This is only available on our Pioneer Package, please head over to the subscriptions area to upgrade. <span v-if="isTrial" class="red"><strong>Want to try this for FREE?</strong> <br>Upgrade to the Pioneer 500 package and continue your FREE Trial (card required for Pioneer trial).</span></p>

							</div>

						</div>

					</form>
					
					<div class="progress-buttons" v-if="!dataDiveLimitReached">

						<span :class="{ 'large-button':true, 'flip-colours': true }" @click="back" v-html="'Back'" v-if="step>1" />
						<span :class="{ 'large-button':true, 'flip-colours': true, disabled: !currentStepValid }" @click="proceed()" v-html="proceedLabel" />

					</div>

				</div>

				<div class="modal-inner-scroll" v-if="projectCreated">

					<div class="project-created">

						<div>

							<h3 v-html="projectCreatedText.title" />
							<p  v-html="projectCreatedText.text" />

							<p v-if="needsActiveSubscription">
								Please head over to the <router-link target='_blank' to="/account/subscriptions">subscriptions page</router-link>.
							</p>

						</div>

						<Icon :type="projectCreatedText.icon" class="svg" v-if="!!projectCreatedText.icon" />

					</div>

				</div>

			</div>

		</div>

	</div>

</template>

<script>
import Vue from 'vue';
import { mapMutations, mapState, mapActions } from 'vuex'
import ApiService from './../services/api.service'
import FormInput from './Form/Input.vue'
import Tip from './Tip.vue'
import VueGoogleAutocomplete from 'vue-google-autocomplete'
import GoogleConnect from './GoogleConnect.vue'
import Loading from './Loading.vue'
import MultiTypes from './Display/ProjectSetupTypes.vue'
import TickCross from './Display/TickCross.vue'
import Keywords from './Keywords.vue'
import Switcher from './Switcher.vue'
import Icon from './Display/Icon.vue'
import general from '../utils/general';
export default {
	name: 'ProjectSetup',
	components: {
		FormInput,
		Tip,
		VueGoogleAutocomplete,
		GoogleConnect,
		Loading,
		TickCross,
		MultiTypes,
		Keywords,
		Switcher,
		Icon
	},
	props: {
		copyFrom: {
			type: Object,
			default: () => { return {} }
		},
		projectInfo: {
			type: Object
		},
		updateType: {
			type: String,
			default: ''
		},
		enabled: {
			type: Object,
			default: () => { return {} }
		}
	},
	data() {

		return {
			hasProjectSelection: false, // new layout if false
			showInsightsMessage: false,
			insightsMessageShown: false,
			projectCreated: false,
			csvUploaded: false,
			rankTrackingKeywordsOnly: false,
			alerts: [],
			hideLanguage: false,
			hideLocation: false,
			includeAlerts: {},
			error: '',
			errors: {},
			urlError: '',
			step: 1,
			autoCompleteKey:1,
			stepValid: false,
			stepInvalidMessage: '',
			urlChecked: {},
			dataDiveChecked: {},
			loading: '',
			gscComplete: {},
			timer: null,
			timer2: null,
			level: '',
			locations:[],
			languages: [],
			extensions: [],
			showAddressSearch: true,
			showCityStreet: false,
			keywordEntryType: 'input',
			newUser: false,
			currentSubscription: true,
			subscriptionLevel: 1,
			keywordsKey: 1,
			copyFromInit: false,
			dataDiveAllowance: 0,
			dataDiveLimitReached: false,
			keywordIdeas: [],
			keywordIdeasChecked: false,
			keywordIdeasChecking: false,
			keywordIdeasAbortController: null,
			keywordsNew: false,
			map: {
				center: { 
					lat: 0, 
					lng: 0 
				},
				options: {
					zoomControl: true,
					mapTypeControl: false,
					scaleControl: false,
					streetViewControl: false,
					rotateControl: false,
					fullscreenControl: true,
					disableDefaultUi: false,
					scrollwheel: false
				} 
			},
			project: {
				searchTypes: [],
				googleAccount: null,
				analyticsViewId: null,
				devices: [],
				keywords: [],
				csvId: null,
				locationZoom: 15,
				locationCode: null,
				languageCode: '',
				frequency: 'daily',
				name: '',
				url: '',
				_ids: [], 
				excludeAlerts: [],
				contentKeywords: '',
				emailInsights: true,
				enabled: {
					rankTracking: true,
					dataDive: false,
					contentKeywords: false
				}
			},
			selectedKeywords: [],
			pulseSearchTypes: false,
			pulseDeviceTypes: false
		}
	},
	watch: {
		project: {
			handler: function (val,old) {
				
				if ( this.copyFromInit ) return;
				
				if ( this.lastProjectCode !== 'not-set' && val.locationCode !== this.lastProjectCode ) {

					Vue.set( this.project, 'locationCoordinate', null );
					Vue.set( this.project, 'locationName', null );
					Vue.set( this, 'level', '' );

				}

				if ( !!this.project.emailInsights ) {

					this.insightsMessageShown = true;

				}

				this.lastProjectCode = val.locationCode;

			},
			deep: true
		},
		includeAlerts: {
			handler: function () {

				let excludeAlerts = [];
				
				Object.keys( this.includeAlerts ).forEach( function ( alertId ) {

					if ( !this.includeAlerts[alertId] ) {

						excludeAlerts.push( alertId )

					}

				}.bind(this));

				this.project.excludeAlerts = excludeAlerts;

			},
			deep: true
		},
		level: {
			handler: function () {

				if ( this.copyFromInit ) return;
				
				this.changeCity();
				++this.autoCompleteKey;

			},
			deep: true
		},
	},
	computed: { 

		...mapState('general',['analysisCreditsAccess']),

		openingTitle () {

			if ( this.project.enabled.rankTracking ) {

				return 'Create a Rank Tracking Project';

			}
			
			if ( this.project.enabled.dataDive ) {

				return 'Add a Data Dive Domain';

			}
			

		},

		copyingAndHasGSC () {

			return this.isCopyingProject && !!this.copyFrom.analyticsViewId && !!this.copyFrom.googleAccount;

		},

		isTrial () {
			
			return !!this.analysisCreditsAccess.trial;

		},

		domain () {

			if ( this.projectInfo ) {

				return this.projectInfo.url;

			}
			else {

				return this.project.url;

			}

		},

		needsActiveSubscription () {

			return this.project.enabled['rankTracking'] && !this.currentSubscription;

		},

		projectCreatedText () {

			let r = { title: '', text: '', icon: 'coffee-biscuit' },
				gatheringData = true;

			if ( this.isEditingProject ) {

				gatheringData = true;

				if ( 
					this.typeProcessed === 'rankTracking' && 
					this.keywordEntryType !== 'upload' && 
					!this.project.keywords ) {

					gatheringData = false;

				}

				r.title = this.typeLabel + ' has been added to your project';

				if ( this.typeProcessed === 'rankTracking' && this.needsActiveSubscription ) {

					r.text = "But you currently don't have an active subscription so your keywords will not be checked.";

				}
				else {

					if ( gatheringData ) {

						r.text = "We're pulling in your data and putting your "+this.typeLabel+" together. <br>Grab a coffee and maybe a cheeky biscuit.<br>Close this window and keep and eye on the projects dashboard for updates.";

					}
					else {

						r.text = "We're pulling in your data and putting your "+this.typeLabel+" together. <br>Grab a coffee and maybe a cheeky biscuit.";

					}

				}

			}
			else {

				if ( 
					!this.project.enabled.dataDive && 
					!this.project.enabled.contentKeywords && 
					this.keywordEntryType !== 'upload' && 
					!this.project.keywords 
				) {

					gatheringData = false;

				}

				if ( this.creatingMultipleProjects.count > 1 ) {

					r.title = gatheringData ? 'Your projects are almost ready' : "Your projects are ready to go";

					if ( this.needsActiveSubscription ) {

						r.text = "But you currently don't have an active subscription so your keywords will not be checked.";

					}
					else {

						r.text = gatheringData ? "We're pulling in your data and putting your projects together. <br>Grab a coffee and maybe a cheeky biscuit." : "Your projects are setup and ready to go, don't forget to add some keywords.";

					}

				} else {

					if ( !this.project.enabled.dataDive ) {

						r.title = gatheringData ? 'Your project is almost ready' : "Your projects are almost ready";

					}
					else {

						r.title = "We're pulling in your data...";

					}

					if ( this.needsActiveSubscription ) {

						r.text = "But you currently don't have an active subscription so your keywords will not be checked.";
					
					}
					else {

						if ( !this.project.enabled.dataDive ) {

							r.text = gatheringData ? "We're pulling in your data and putting your project together. <br>Grab a coffee and maybe a cheeky biscuit." : "Your project is setup and ready to go, don't forget to add some keywords.";

						}
						else {
							
							r.text = "This can take a little while, especially for larger sites. <br>Grab a coffee and maybe a cheeky biscuit.";
							
						}

					}

				}

			}

			return r;

		},

		svg () {

			return {
				dataDive :{
					viewBox: "0 0 448 512",
					paths: '<path data-v-61dd7a3d="" d="M349.4 44.6c5.9-13.7 1.5-29.7-10.6-38.5s-28.6-8-39.9 1.8l-256 224c-10 8.8-13.6 22.9-8.9 35.3S50.7 288 64 288H175.5L98.6 467.4c-5.9 13.7-1.5 29.7 10.6 38.5s28.6 8 39.9-1.8l256-224c10-8.8 13.6-22.9 8.9-35.3s-16.6-20.7-30-20.7H272.5L349.4 44.6z"></path>'
				},
				rankTracking: {
					viewBox: "0 0 315 268.1",
					paths: '<path class="st0" d="M268,0H47C21,0,0,21,0,47l0,0v174.1c0,25.9,21.1,47,47,47h221c25.9,0,47-21,47-47V46.9C314.9,21,293.9,0,268,0zM268,220.5l-220.4,0.6V47.6l219.7-0.6L268,220.5z"/><rect x="69.6" y="133.4" class="st0" width="51" height="68.4"/><rect x="132" y="103.1" class="st0" width="50.9" height="98.7"/><rect x="194.4" y="72.8" class="st0" width="50.9" height="129"/>'
				},
				contentKeywords: {
					viewBox: "0 0 299.6 256.4",
					paths: '<path d="M200.2,85.3c-7.9-16.8-22.2-25.9-41.5-26.2c-10-0.3-19.8,2.8-27.9,8.6c-7.3,5.4-12.9,12.9-16,21.4c-0.5,1.4-1,2.8-1.5,4.4l-97.2,0.6c-7.6,0.1-13.5,4.4-15.5,11.3c-1.3,4.7-0.4,9.7,2.5,13.6c2.3,3.1,5.8,5.2,9.6,5.9c0,2.2,0,4.5,0.1,6.7c0.1,6.1,3.4,9.8,9.4,10.5c1.7,0.2,3.4,0.2,5.2,0.2h1c0.8,0,1.6,0,2.3,0.1c2.8,0.1,5.6,0.1,8.4-0.1c7.2-0.7,10.6-4.6,10.6-11.8v-5.7l62.1-0.3c0.4,1.9,0.7,3.7,1.2,5.4c2.1,8.5,6.4,16.2,12.5,22.5c8.4,8.5,19.1,12.9,31.7,13.2h1.3c8.8,0.1,17.5-2.2,25-6.9c8.5-5.2,14.9-13.2,18.2-22.6C208.3,118.6,207.7,101.4,200.2,85.3z M174.8,123.7c-1.1,3.3-3.2,6.1-6,8.1c-3.2,2.2-7,3.2-10.8,3l0,0c-4.4,0.1-8.7-1.6-11.8-4.6c-1.3-1.3-2.4-2.8-3.2-4.4c-3.9-8.3-4.1-17.8-0.4-26.2c2.5-5.9,6.8-8.9,13.6-9.6c0.8-0.1,1.6-0.1,2.4-0.1c7.7,0,13.6,4.2,16.3,11.6C177.4,108.7,177.4,116.6,174.8,123.7z"/><path d="M299.6,237.3c0,5-2,9.9-5.6,13.4c-3.6,3.6-8.5,5.7-13.7,5.7c-5,0-9.8-2-13.4-5.5c-4.9-5-18.5-18.4-29.5-29.2c-9.7-9.5-16.5-16.3-20.6-20.4c-5.2,3.4-10.6,6.4-16.3,8.8c-15.4,6.5-30.8,9.9-45.7,9.9l0,0c-20.4,0-40.4-6.2-59.4-18.4c-15.6-9.9-28.2-23.8-36.6-40.3l-3.8-7.5h42.2l1.6,2c25.2,32.1,71.6,37.8,103.8,12.6c32.1-25.2,37.8-71.6,12.6-103.8C190,32.5,143.6,26.8,111.4,52c-7.7,6-14.1,13.4-18.8,21.9l-1.5,2.6H51.1l2.9-7.1c2.8-7,6.3-13.8,10.4-20.2c11.5-18,27.8-31.6,48.4-40.4c1.8-0.8,3.7-1.5,5.5-2.3l3-1.2l14.2-3.8l6.5-0.6c4.3-0.4,8.8-0.8,13.2-0.9c60.5-0.9,110.3,47.3,111.3,107.8c0.1,5-0.2,9.9-0.8,14.9c-2.1,19-9.4,37-21,52.2c4.9,4.9,12.3,12.2,19.5,19.3l3.7,3.6c10.8,10.7,21.9,21.7,26.3,26.1C297.7,227.4,299.7,232.3,299.6,237.3z"/>'
				}
			}

		},

		isCopyingProject () {

			return Object.keys( this.copyFrom ).length > 0;

		},
		typeProcessed () {

			if ( !!this.updateType ) {

				let t = this.updateType.split('-');

				if ( t.length > 1 ) {

					t[1] = general.capitalizeFirstLetter( t[1] );

				}

				return t.join('');

			}

			if ( this.enabled.rankTracking ) return 'rankTracking';
			if ( this.enabled.dataDive ) return 'dataDive';

			return null;

		},
		isEditingProject () {

			return ( !!this.updateType && !!this.projectInfo && !!this.projectInfo._id ) ? this.projectInfo._id : false;

		},

		keywordsLabelOverride () {

			if ( this.isCopyingProject ) {

				return 'All keywords will be copied across from '+this.copyFrom.name+', if you\'d like to add any extra please enter them below.';

			}

			return '';

		},

		frequencyLabelOverride () {

			if ( this.isCopyingProject ) {

				return 'If you are adding any additional keywords, how often should these be checked? (Any keywords copied from '+this.copyFrom.name+' will also copy over the frequency).';

			}

			return '';

		},

		allIsGood () {

			return this.step === this.totalSteps;

		},

		typeLabel () {

			switch ( this.typeProcessed ) {

				case 'rankTracking' :

					return "Rank Tracking";

				case 'contentKeywords' :

					return "Content & Keywords";

				case 'dataDive' :
					
					return "Data Dive";

			}

			return '';

		},

		theLevel () {

			return this.level;

		},

		country () {

			if ( this.project.locationCode ) {

				const found = this.locations.find( location => location._id === this.project.locationCode );

				if ( typeof found !== 'undefined' ) {

					return found.code;

				}

			}

			return null;

		},

		multiTypes () {

			return {
				search: [
					{
						type: 'organic',
						logo: require('./../assets/img/Google-Logo.svg'),
						label: 'Organic'
					},
					{
						type: 'local',
						logo: require('./../assets/img/Google_Maps-Logo.svg'),
						label: 'Local'
					}
				],
				devices: [
					{
						type: 'desktop',
						icon: 'desktop',
						label: 'Desktop'
					},
					{
						type: 'mobile',
						icon: 'mobile-alt',
						label: 'Mobile'
					}
				],
				map: [
					{
						type: 'street',
						icon: 'road',
						label: 'Street'
					},
					{
						type: 'city',
						icon: 'city',
						label: 'City/Area'
					}
				],
				keywords: [
					{
						type: 'input',
						icon: 'keyboard',
						label: 'Type/Paste'
					},
					{
						type: 'upload',
						icon: 'upload',
						label: 'Upload CSV'
					}
				]
			}

		},

		gscDone () {

			return !!this.gscComplete[this.project.url];

		},

		projectType () {

			if ( this.project.enabled.dataDive ) return 'dataDive';
			if ( this.project.enabled.rankTracking ) return 'rankTracking';
			if ( this.project.enabled.contentKeywords ) return 'contentKeywords';

		},

		title () {

			if ( this.typeProcessed === 'dataDive' ) return 'Add a Domain to Data Dive';

			return !!this.updateType ? 'Update Project' : 'Rank Tracking';

		},
		allowed () {

			return {

				dataDive: this.dataDiveAllowance > 0,
				rankTracking: true,
				contentKeywords: this.subscriptionLevel > 1 || window.location.host === 'localhost:8080'

			}

		},
		projectTypes () {

			return [
				{
					name: 'dataDive',
					title: 'Data Dive',
					description: this.isCopyingProject ? 'Your Google Search Console and Analytics accounts are already connected for insights and opportunities.' : 'Connect your Google Search Console and Analytics for insights and opportunities.',
					notAllowed: this.allowed.dataDive ? false : "You've hit your limit for this package!",
					video: 'data-dive'
				},
				{
					name: 'rankTracking',
					title: 'Rank Tracking',
					description: 'Track keywords and monitor for insights, issues and opportunities.',
					notAllowed: !this.allowed.rankTracking,
					video: 'rank-tracking'
				},
				{
					name: 'contentKeywords',
					title: 'Content & Keywords',
					description: 'Find endless content ideas and keyword opportunities.',
					notAllowed: this.allowed.contentKeywords ? false : "Pioneer packages only!",
					video: 'content-and-keywords'
				}
			];

		},
		totalSteps () {

			return this.theSteps.length;

		},
		theSteps () {

			let steps = [];

			if ( !this.updateType ) steps.push('init');

			if ( this.project.enabled.dataDive && !this.copyingAndHasGSC ) steps.push('google');
			if ( this.project.enabled.rankTracking && !this.rankTrackingKeywordsOnly ) steps.push('tracking-location');
			if ( this.project.enabled.rankTracking ) steps.push('keywords-to-track');
			if ( this.project.enabled.contentKeywords ) steps.push('content-keywords');
			if ( this.project.enabled.rankTracking || this.project.enabled.dataDive ) {
				if ( !this.isEditingProject || !this.projectInfo.emailInsights ) {
					steps.push('email-insights');
				}
			}
			if ( this.project.enabled.rankTracking && this.alerts.length > 0 && !this.rankTrackingKeywordsOnly ) steps.push('alerts');

			return steps;

		},
		currentStep () {

			return this.theSteps[this.step-1];

		},

		proceedLabel () {

			if ( this.currentStep === 'keywords-to-track' && !this.keywordEntryType && !this.isCopyingProject ) return 'Skip';
			if ( this.currentStep === 'keywords-to-track' && this.keywordEntryType === 'upload' && !this.currentStepValid ) return 'Skip';

			let p = 'Proceed';

			if ( this.allIsGood ) {

				if ( !!this.typeProcessed ) {

					switch ( this.typeProcessed ) {

						case 'rankTracking' :
						case 'dataDive' :

							return "Add " + this.typeLabel;

						case 'contentKeywords' :

							if ( this.allowed.contentKeywords ) return "Add " + this.typeLabel;
							
							return "Head over to Subscriptions";

					}
					
					return 'Update Project'; 

				}
				else {
				
					p = this.projectType === 'rankTracking' ? 'Create Project' : 'Add Domain';

					if ( this.creatingMultipleProjects.count > 1 ) p += 's';

				}

			}

			return p;

		},
		currentStepValid () {

			if ( !!this.typeProcessed && this.typeProcessed === 'contentKeywords' && !this.allowed.contentKeywords ) return true;

			switch ( this.currentStep ) {

				case 'keywords-to-track' :

					if ( this.keywordEntryType === 'upload' && !this.csvUploaded ) return false;

			}

			return this.valid();

		},
		creatingMultipleProjects () {

			let c = [];

			this.project.searchTypes.forEach ( function (s) {

				this.project.devices.forEach ( function (d) {

					let t = this.project.name;

					if ( s === 'local' && this.project.searchTypes.length > 1 ) {

						t += ' - Local';

					}

					c.push( t + ' - ' + d.charAt(0).toUpperCase() + d.slice(1) );

				}.bind(this));

			}.bind(this));

			if ( c.length > 1 ) {

				return { text: this.joinArrayWithCommasAndAmpersand(c), count: c.length };

			}

			return { count: 0 };

		},
		alertsDescription () {

			return "Would you like to enable any of your alerts for this project (any that are currently enabled for all projects are toggled on by default)?";

		},
		hasDataDiveAlready () {

			if ( !this.project.url ) return false;

			try {

				let u = new URL( this.project.url );

				return !!this.dataDiveChecked[u.hostname];

			}
			catch (e) {

				return false;

			}

		}

    },

	methods: {

		...mapMutations('general',['updateLoadingText','updateVar']),
		...mapActions("general", [
			"playVideo",
		]),

		showVideo (e,v) {

			e.stopPropagation();
			e.preventDefault();

			this.playVideo(v);

		},

		keywordsChanged (info) {
			
			this.project.csvId = ( !!info.csvId && !!info.csvConfirmed ) ? info.csvId : null;

			Vue.set( this.project, 'csvId', ( !!info.csvId && !!info.csvConfirmed ) ? info.csvId : null );

			if ( !!info.csvId ) {
				this.csvUploaded = !!info.csvConfirmed;
			}

			this.project.frequency = info.frequency;

			if ( !this.project.csvId ) {

				this.project.keywords = info.keywords.split("\n");

			}
			else {

				this.project.keywords = [];

			}

		},

		changeCity () {
			
			this.showAddressSearch = true;
			Vue.set( this.project, 'locationCoordinate', null );
			Vue.set( this.project, 'locationName', null );

		},

		setMapLevel( level ) {
			
			if ( level === this.level ) this.level = null;
			else this.level = level;
			this.project.locationZoom = 15;

		},

		setMapLocation(position) {

			this.setLocation ( position.latLng.lat(), position.latLng.lng() )

		},

		setLocation ( lat, lng ) {
			
			Vue.set( this.project, 'locationCoordinate', { latitude: lat, longitude: lng } );
			Vue.set( this.map, 'center', { lat: lat, lng: lng } );

		},

		getAddressData (addressData, placeResultData, id) {

			//placeResultData.geometry.viewport
			let mapId = this.level === 'street' ? 'mapStreet' : 'mapCity';

			Vue.set( this.project, 'locationName', document.getElementById(mapId).value );

			document.getElementById(mapId).value = '';

			this.setLocation ( addressData.latitude, addressData.longitude );

            this.showAddressSearch = false;

			if ( placeResultData.geometry && placeResultData.geometry.viewport ) {

				setTimeout( function () {

					if ( this.$refs.mapRef ) {

						this.$refs.mapRef.fitBounds(placeResultData.geometry.viewport);

					}

				}.bind(this), 500 );

			}

        },

		joinArrayWithCommasAndAmpersand(arr) {
			if (arr.length === 0) {
				return '';
			} else if (arr.length === 1) {
				return arr[0];
			} else {
				return arr.slice(0, -1).join(', ') + ' & ' + arr[arr.length - 1];
			}
		},

		googleLoadingChange (msg) {

			this.loading = msg;

		},

		googleConnectComplete(info) {

			this.loading = '';
			
			if ( info.status === 'success' || info.status === 'clear' ) {

				let url = this.isEditingProject ? this.projectInfo.url : this.project.url;

				Vue.set( this.gscComplete, url, info.status === 'clear' ? null : info.extra );

			}
			else if ( typeof info.msg !== 'undefined' ) {

				this.$snotify[info.status]( info.msg, 'Google' );

			}

		},

		selectProjectType (projectType) {

			const type = projectType.name;

			let p = this.projectTypes.find( function (t) { return t.name === type; } );

			if ( !!p.notAllowed ) {

				this.$snotify.error( p.notAllowed, projectType.title );
				
				return false;

			}

			this.project.enabled[type] = !this.project.enabled[type];

		},

		close (info) {

			if (this.keywordIdeasAbortController) {
				this.keywordIdeasAbortController.abort();
			}

			if ( this.projectCreated ) {

				switch ( this.typeProcessed ) {

					case 'rankTracking' :
					case 'rank-tracking' :

						return this.$router.push( '/rank-tracking/' + this.projectCreated );

					case 'dataDive' :
					case 'data-dive' :

						return this.$router.push( '/data-dive/' + this.projectCreated + '/url' );

				}

			}

			this.$emit( 'close' ,info );

		},

		hasInternalErrors(a) {

			this.errors[a.name] = a.error;

		},

		valid (step,addPulse) {

			let res = true, 
				updateStepValid = false;

			if ( typeof step === 'undefined' || step === null ) {

				updateStepValid = true;
				step = this.step;
				this.stepInvalidMessage = "";

			}

			switch (this.currentStep) {

				case 'init' :

					res = !!this.project.enabled.rankTracking || !!this.project.enabled.dataDive || !!this.project.enabled.contentKeywords;

					if ( !res ) {

						this.stepInvalidMessage = "What do you want to use this project for?";
						
						break;

					}

					let fields = ['url','languageCode'];

					if ( this.project.enabled.rankTracking ) fields = ['url','name'];

					fields.forEach( function (f) {
						
						if ( !res ) return;

						res = this.fieldOk(f);

					}.bind(this) );

					if ( !res ) {

						this.stepInvalidMessage = "Please enter both fields.";

					}

				break;

				case 'google' :

					if ( this.project.enabled.dataDive ) {

						let url = this.isEditingProject ? this.projectInfo.url : this.project.url;

						res = !!this.gscComplete[url];

						this.stepInvalidMessage = "Please add your Google account";

					}

				break;

				case 'content-keywords' :

					if ( this.isEditingProject ) {

						res = !!this.project.contentKeywords;

						this.stepInvalidMessage = "Please add some keywords/topics";

					}

					if ( res && !!this.project.contentKeywords && !this.project.locationCode && !this.project.enabled.rankTracking && !this.hideLocation ) {

						res = false;
						this.stepInvalidMessage = "Please select a location";

					}

				break;

				case 'tracking-location' :
 
					res = this.project.searchTypes.length > 0 && this.project.devices.length > 0 && !!this.project.locationCode;

					if ( !res ) {

						if ( !this.project.locationCode ) this.stepInvalidMessage = "Please select a location";
						if ( !this.project.devices.length ) {
							this.stepInvalidMessage = "Please select a device type";
							if ( addPulse ) this.pulseDeviceTypes = true;
						}
						if ( !this.project.searchTypes.length ) {
							this.stepInvalidMessage = "Please select a search type";
							if ( addPulse ) this.pulseSearchTypes = true;
						}
						
						break;

					}

					if ( this.project.searchTypes.indexOf('local') >= 0 ) {

						res = !!this.level && !!this.project.locationName;

						if ( !res ) {

							this.stepInvalidMessage = "Please add a city or street.";

						}

					}

				break;

			}

			if ( updateStepValid ) this.stepValid = res;

			return res;

		},

		fieldOk (name,canBeEmpty) {

			if ( typeof canBeEmpty === 'undefined' && ( typeof this.project[name] === 'undefined' || this.project[name] === '' ) ) return false;

			let t = typeof this.errors[name] === 'undefined' || this.errors[name] === false;

			if ( !!t && name === 'url' ) {

				try {
					new URL(this.project.url);
					const UrlPattern = require('url-pattern');
					const pattern = new UrlPattern('http(s)\\://(*.):domain.:tld(\\::port)(/*)');
					const match = pattern.match( this.project.url );
					return !!match;
				} catch (err) {
					return false;
				}

			}

			return t;

		},

		urlIsChecked ( url, val, dataDiveProject ) {

			let u = new URL(url);

			if ( typeof val !== 'undefined' ) {
				
				this.urlChecked[u.hostname] = val;
				Vue.set(this.dataDiveChecked, u.hostname, dataDiveProject);

			}

			return !!this.urlChecked[u.hostname];

		},

		back () {

			this.proceed ( true );

		},

		multiTypeClicked( info ) {

			switch (info.type) {

				case 'searchTypes' :

					this.setAType( info.type, info.info.type );
					this.pulseSearchTypes = false;

				break;

				case 'devices' :

					this.setAType( info.type, info.info.type );
					this.pulseDeviceTypes = false;

				break;

				case 'map' :

					this.setMapLevel( info.info.type )

				break;

				case 'keywords' :

					this.keywordEntryType = info.info.type;
					++this.keywordsKey;

				break;

			}

		},

		setAType( type, value ) {

			if ( this.isEditingProject ) {

				if ( !this.project[type][0] || this.project[type][0] !== value ) {

					this.project[type] = [value];
					if ( type === 'searchTypes' ) {
						
						this.level = '';

					}

				}

				return;

			}

			let s = this.project[type].indexOf( value );

			if ( type === 'searchTypes' ) {
				
				this.level = '';
				this.project[type] = [];

			}

			if ( s >= 0 ) {

				this.project[type].splice(s,1);

			}
			else {

				this.project[type].push( value );

			}

			if ( type === 'devices' && this.project.devices.length > 1 ) {

				this.$dialog
					.confirm('This will create 2 projects so any keywords added in the next stage will count twice against your tracking allowance.', { okText: "That's fine", cancelText: "Select one device" })
					.then(function() {})
					.catch(function() {
						this.project[type] = [];
					}.bind(this));

			}

		},

		loadProjects () {

			this.$emit('loadProjects');

		},

		createProject () {

			if ( this.projectCreated ) return;

			this.loading = this.typeProcessed === 'rankTracking' ? 'Creating your project...' : 'Adding your domain...';

			let info = general.stripObject( this.project ), 
				action = 'post';

			if ( this.creatingMultipleProjects.count > 1 ) {
				
				this.loading = 'Creating ' + this.creatingMultipleProjects.count + ' projects...';

			}

			let errorTitle = this.isEditingProject ? 'Update Project' : 'Create Project';

			if ( this.isEditingProject ) {

				this.loading = 'Updating your project...';

				action = 'put';

				info = {};

				switch ( this.typeProcessed ) {

					case 'rankTracking' :

						this.loading = 'Adding Rank Tracking to your project...';

						info = {
							locationCode: this.project.locationCode,
							locationName: this.project.locationName,
							locationZoom: this.project.locationZoom,
							locationCoordinate: this.project.locationCoordinate,
							languageCode: this.project.languageCode,
							searchType: this.project.searchTypes[0],
							device: this.project.devices[0],
							frequency: this.project.frequency,
							csvId: this.project.csvId,
							keywords: this.project.keywords,
							excludeAlerts: this.project.excludeAlerts
						};

					break;

					case 'dataDive' :

						this.loading = 'Adding Data Dive to your project...';

					break;

					case 'contentKeywords' :
						
						info = {
							contentKeywords: this.project.contentKeywords
						};

						if ( this.project.languageCode ) {

							info.languageCode = this.project.languageCode;

						}

						if ( this.project.locationCode ) {

							info.locationCode = this.project.locationCode;

						}

					break;

				}

				info.updateType = this.typeProcessed;
				info._ids = [this.projectInfo._id];

			}
			
			if ( this.project.enabled.dataDive ) {

				let url = this.isEditingProject ? this.projectInfo.url : this.project.url;

				if ( !this.isEditingProject ) {

					info.name = info.url;

				}

				if ( !!this.copyFrom.googleAccount ) {

					info.googleAccount = this.copyFrom.googleAccount;
					info.analyticsViewId = this.copyFrom.analyticsViewId;

				}
				else {

					info.googleAccount = this.gscComplete[url].googleAccount;
					info.analyticsViewId = this.gscComplete[url].analyticsViewId;

				}

			}

			if ( !!this.copyFrom && Object.keys( this.copyFrom ).length > 0 ) {

				Vue.set( this.project, 'duplicate', this.copyFrom._id );
				info.duplicate = this.copyFrom._id;

			}

			if ( this.project.enabled.rankTracking ) {

				if ( this.keywordEntryType === 'upload' ) {

					if ( info.keywords ) delete info.keywords;

				}
				else if ( info.csvId ) delete info.csvId;

			}

			ApiService[action]( 'projects', info )
			.then( function( info ) {

				this.loading = '';

				if ( !this.isEditingProject ) {

					let firstKeywords = false;

					info.data.forEach ( function (project) { 

						this.project._ids.push(project._id);

						if ( !firstKeywords ) {

							firstKeywords = !!project.keywords;

						}

					}.bind(this));

				}

				this.projectCreated = info.data[0]._id;

				this.loadProjects();

			}.bind(this))
			.catch( function(err) {

				this.$snotify.error(err.response.data.error, errorTitle);

				this.loading = '';
			
			}.bind(this));

		},

		proceed ( goBack = false ) {

			const n = goBack ? -1 : 1;

			if ( !!this.typeProcessed && this.typeProcessed === 'contentKeywords' && !this.allowed.contentKeywords ) {

				this.$router.push('/account/subscriptions/');
				return;

			}

			if ( this.hasDataDiveAlready && !!this.project.enabled.dataDive && !goBack ) {

				this.$snotify.error('You already have Data Dive connected to this domain, you can\'t connect another one.', 'Data Dive');
				return;

			}

			this.valid ( null, true );

			if ( !this.stepValid && !goBack ) {

				if ( this.stepInvalidMessage ) {

					this.$snotify.error( this.stepInvalidMessage, 'Error' );

				}

				return false;

			}

			if ( this.allIsGood && !goBack ) {

				this.createProject();
				return;

			}
			
			if (this.keywordIdeasAbortController) {
				this.keywordIdeasAbortController.abort();
			}

			switch ( this.currentStep ) {

				case 'init' :

					if ( !this.urlIsChecked( this.project.url ) ) {

						this.loading = 'Checking ' + this.project.url + '...';
						this.urlError = '';

						ApiService.post( 'projects/check-url', { url: this.project.url } )
						.then( function(info) {

							this.loading = '';
							this.urlIsChecked( this.project.url, true, info.data.dataDiveProject );
							this.step = 2;

						}.bind(this))
						.catch( function(err) {

							let info = ( !!err.response && !!err.response.data && !!err.response.data.error ) ? err.response.data.error : '', dataDiveProject = null;

							if ( typeof info === 'object' ) {

								this.urlError = info.message;

								if ( typeof info.type !== 'undefined' && info.type === 'redirect' ) {

									this.urlIsChecked( this.project.url, true, dataDiveProject );
									this.project.url = info.new;
									this.urlIsChecked( this.project.url, true, dataDiveProject );

								}

								dataDiveProject = info.dataDiveProject;

							}
							else {

								++this.step;

							}

							this.loading = '';
						
						}.bind(this));

					}
					else {

						this.step += n;

					}

					if ( this.project.enabled.rankTracking ) {

						if ( this.project.languageCode === '' ) {

							Vue.set( this.project, 'languageCode', 'en' );

						} 

					}

				break;

				case 'tracking-location': // seedsOrUrl, type = "keyword", countryCode, locationCode

					if ( !this.keywordIdeasChecked && !this.keywordIdeasChecking ) {

						this.keywordIdeasChecking = true;
						this.keywordIdeasAbortController = new AbortController();

						ApiService.post( 'googlea/keyword-ideas', {
							seedsOrUrl: this.project.url,
							type: 'domain',
							countryCode: this.project.locationCode,
							pageSize: 50
						},
  						{ signal: this.keywordIdeasAbortController.signal } )
						.then( function( info ) {

							this.keywordIdeas = info.data;
							this.keywordIdeasChecked = true;
							this.keywordIdeasChecking = false;

							this.keywordIdeas.forEach(function(keyword,index) {

								if ( index < 5 ) {

									//this.toggleKeyword(keyword.keyword,false);

								}

							}.bind(this));

							++this.keywordsKey;

						}.bind(this))
						.catch( function(err) {
							this.keywordIdeasChecking = false;
							if (err.name === 'CanceledError' || err.name === 'AbortError' || err.name === 'TypeError' ) {
								this.keywordIdeasChecked = false;
								return;
							}
							this.keywordIdeasChecked = true;
							
						}.bind(this));

					}
					
					this.step += n;

				break;

				case 'email-insights' :

					if ( !this.isEditingProject && !this.insightsMessageShown ) {

						if ( this.showInsightsMessage ) {

							this.insightsMessageShown = true;
							this.showInsightsMessage = false;

						}
						else {

							this.showInsightsMessage = true;
							break;

						}

					}

					this.step += n;

				break;

				default:

					this.step += n;

			}

		},
		toggleKeyword(keyword,reloadKeywords=true) {
			if ( reloadKeywords ) this.keywordsNew = false;
			const index = this.selectedKeywords.indexOf(keyword);
			const index2 = this.project.keywords.indexOf(keyword);
			if (index === -1) {
				this.selectedKeywords.push(keyword);
				if (index2 === -1 && keyword.trim() !== '') {
					this.project.keywords.push(keyword);
				}
			} else {
				this.selectedKeywords.splice(index, 1);
				if (index2 !== -1) {
					this.project.keywords.splice(index2, 1);
				}
			}
			
			// Filter out any empty strings
			this.project.keywords = this.project.keywords.filter(kw => kw.trim() !== '');
			
			if ( reloadKeywords ) ++this.keywordsKey;
		},
	},
	created() {

		if ( Object.keys( this.enabled ).length > 0 ) {

			Object.keys( this.enabled ).forEach( function (t) {

				Vue.set( this.project.enabled, t, this.enabled[t]);

			}.bind(this));

		}
		
		if ( this.isEditingProject ) {

			Object.keys(this.project.enabled).forEach( function (t) {

				Vue.set( this.project.enabled, t, this.typeProcessed === t );

			}.bind(this));
			
			if ( this.projectInfo.hasRankTracking ) {

				if ( this.typeProcessed === 'contentKeywords' ) {

					this.hideLanguage = !!this.projectInfo.languageCode;
					this.hideLocation = !!this.projectInfo.locationCode;

				}

				if ( this.typeProcessed === 'rankTracking' ) {

					this.rankTrackingKeywordsOnly = true;

				}

			}

		}

		if ( !!this.copyFrom && Object.keys( this.copyFrom ).length > 0 ) {

			this.copyFromInit = true;

			if ( this.copyFrom.locationCode ) this.lastProjectCode = this.copyFrom.locationCode;

			if ( !!this.copyFrom.locationCoordinate ) {

				this.setMapLevel( 'city' );

			}

			['name','url','locationCode','locationName','locationCoordinate','languageCode','extension','frequency'].forEach ( function (k) {

				if ( this.copyFrom[k] ) Vue.set( this.project, k, this.copyFrom[k]);

			}.bind(this));

			this.project.searchTypes = [this.copyFrom.searchType];
			this.project.devices = [this.copyFrom.device];

			if ( !!this.copyFrom.locationCoordinate ) {

				this.showAddressSearch = true;
				this.setLocation ( this.copyFrom.locationCoordinate.coordinates[1], this.copyFrom.locationCoordinate.coordinates[0] );

			}

			setTimeout( function (){ this.copyFromInit = false; }.bind(this), 100 );

		}

		ApiService.get( 'projects/option-lists' )
		.then( function( info ) {

			Object.keys( info.data ).forEach ( function (a) {

				this[a] = info.data[a];

			}.bind(this));

		}.bind(this))
		.catch( function(err) {}.bind(this));

		this.loading = 'One moment...';

		ApiService.get( 'users', null, 'ProjectSetup' )
		.then( function( info ) {

			['currentSubscription','newUser'].forEach( function (n) {

				this[n] = !!info.data[n];

			}.bind(this));

			this.subscriptionLevel = info.data.subscriptionLevel;
			this.dataDiveAllowance = info.data.dataDiveAllocation.free;

			if ( this.dataDiveAllowance === 0 && !!this.project.enabled.dataDive ) {

				this.project.enabled.dataDive = false;
				this.dataDiveLimitReached = true;

			}

			this.loading = '';
			
		}.bind(this))
		.catch( function(err) {
			console.error(err);
		}.bind(this));

		ApiService.get( 'alerts' )
		.then( function( info ) {

			info.data.items.forEach( function (a) {
				
				if ( !!a.allProjects && !a.disabledByRC && !a.onHold ) {

					Vue.set( this.includeAlerts, a._id, a.frequency !== 'off' && !!a.allProjects );
					this.alerts.push( a );

				}

			}.bind(this));

		}.bind(this))
		.catch( function(err) {}.bind(this));

		ApiService.get( 'google/accounts' )
		.then( function( info ) {

			this.googleAccountsList = info.data;

		}.bind(this))
		.catch( function() {}.bind(this));

	},
	mounted () {

		this.timer2 = setInterval(() => this.valid(), 100);

	},
	beforeDestroy() {

  		if ( this.timer2 ) {

	  		clearInterval(this.timer2);
	  		this.timer2 = null;

	  	}

  	}
}
</script>

<style lang="less" scoped>
@import './../assets/css/less/_helper.less';
@import './../assets/css/less/_settings.less';
@keyframes pulseAnimation {
    0% { fill: #fff; } 
    50% { fill: @formDefaultColour; }
    100% { fill: #fff; }
}
.project-created {
	padding-top: 30px;
	h3, p {
		padding: 0;
		margin: 0;
		a {
			font-weight: 600;
			color: @formDefaultColour;
			text-decoration: none;
			&:hover {
				text-decoration: underline;
			}
		}
	}
	h3 {
		margin-bottom: 10px;
	}
	.svg {

		margin: 20px auto 0;
		max-width: 200px;
		display: block;

	}
}
.no-subscription {
	background:@red;
	color: #fff;
	padding: 15px;
	text-align: center;
	.rounded(5px);
	margin: 10px 0 0;
	position: relative;
	a, span {
		color: inherit;
	}
	.close {
		width: 20px;
		height: 20px;
		right: -6px;
		top: -6px;
	}
}
.alerts-wrap {
	.alert {
		.alert-name {

			font-size: 16px;
			display: inline-block;
			margin-bottom: 15px;
			padding-top: 4px !important;

			&:last-child {

				margin-bottom: 0;

			}

		}
	}
}
.keyword-adder-wrap {
	margin-top: 20px;

	.keyword-suggestions {
		margin-top: 20px;
		position: relative;
		
		&.loading {
			min-height: 100px;
			width: 400px;
		}
		
		h3 {
			font-size: 18px;
			margin-bottom: 5px;
		}

		.keyword-suggestions-list-wrapper {

			position: relative;
			margin-top: 25px;

			h5 {
				text-align: right;
				margin: 0;
				padding: 0;
				position: absolute;
				top: -20px;
				width: 100%;
			}

		}

		.keyword-suggestions-list {
			margin-top: 15px;
			border: 1px solid fade(@darkContrast, 20%);
			border-radius: 4px;
			
			.keyword-suggestion {
				padding: 12px;
				border-bottom: 1px solid fade(@darkContrast, 20%);
				cursor: pointer;
				display: flex;
				justify-content: space-between;
				align-items: center;
				transition: all 0.2s ease;

				&:last-child {
					border-bottom: none;
				}

				&:hover {
					background-color: fade(@formDefaultColour, 5%);
				}

				&.selected {
					background-color: fade(@formDefaultColour, 10%);
				}

				.keyword-info {
					.keyword {
						display: block;
						font-weight: 500;
						margin-bottom: 3px;
					}
					.volume {
						font-size: 13px;
						color: fade(@darkContrast, 70%);
					}
				}

				.competition {
					padding: 4px 8px;
					border-radius: 3px;
					font-size: 12px;
					font-weight: 500;

					&.low {
						background-color: #e8f5e9;
						color: #2e7d32;
					}
					&.medium {
						background-color: #fff3e0;
						color: #f57c00;
					}
					&.high {
						background-color: #ffebee;
						color: #c62828;
					}
				}
			}
		}
	}
}
.csv-upload-disabled {
	background-color: @red;
	color: #fff;
	padding: 5px 10px;
	margin-top: 15px;
	border-radius: 5px;
}
.video-message {
	margin-bottom: 20px;
	span {
		font-weight: 500;
		cursor: pointer;
		color: @formDefaultColour;
	}
}
.modal {

	> .modal-wrap {

		> .modal-inner {

			.modal-title {

				margin-bottom: 0;

			}

			.progress-buttons {

				margin-top: 20px;
				.clearfix();

				.large-button {

					margin: 0;

					&:first-child {

						float: left;

					}

					&:last-child {

						float: right;

					}

				}

			}

			> .modal-inner-scroll {

				form {

					h1, h2, h3, h4, p {

						margin: 0;
						padding: 0;

					}

					hr {

						background-color: fade( @darkContrast, 50% );

					}

					.big-heading {

						margin: 16px 0 5px;
						font-size: 30px;

						svg, .svg {

							width: 27px;
							vertical-align: top;
							margin-top: 7px;
    						margin-right: 8px;
							fill: @darkContrast;

						}

					}

					.main-heading {

						margin: 15px 0 2px;
						font-size: 18px;

					}

					p.info {

						margin-bottom: 15px;
						margin-top: 10px;

						.red {

							font-weight: @bold;
							color: @red;
							display: inline-block;
							padding-top: 10px;

						}

						&.msg {

							background-color: @darkContrast;
							color: #fff;
							padding: 10px;

							em {

								font-style: normal;
								font-weight: @bold;
								color: inherit;

							}

						}

					}

					> p, .p-info {

						margin-bottom: 15px;

					}

					.edit {

						color: @formDefaultColour;
						cursor: pointer;

					}

					.input-2-row, .input-1-row {

						.setup-input-wrap {

							margin-bottom: 15px;

							&:last-child {

								margin-bottom: 0;

							}


						}

					}

					.project-types {

						.project-type {

							position: relative;
							background-color: fade( @darkContrast, 80% );
							padding: 10px 0;
							margin-bottom: 10px;
							border-radius: 4px;
							border: 4px solid @darkContrast;
							cursor: pointer;
							.opacity(0.85);

							.added-to-project {

								
								font-size: 12px;
								color: #fff;
								background-color: @darkContrast;
								padding: 4px 6px;
								border-radius: 4px;
								margin-left: 10px;
								margin-bottom: 10px;
								display: inline-block;

								> svg, .svg {

									width: 16px;
									height: 13px;
									fill: #fff;

									&.pulse {

										animation: pulseAnimation 2s infinite;

									}

								}

							}

							&.not-available {

								cursor: not-allowed;
								background-color: fade( @darkContrast, 50% );
								border-color: fade( @darkContrast, 50% );
								padding-bottom: 30px;

								p {

									margin-top: 8px;

								}

							}

							&:not(.not-available) {

								&:hover {

									border-color: @formDefaultColour;
									.opacity(1);

								}

								&.selected {

									border-color: @formDefaultColour;
									background-color: @darkContrast;
									.opacity(1);

								}

							}

							.project-type-overlay {

								position: absolute;
								top: 0;
								left: 0;
								width: 100%;
								height: 100%;
								z-index: 1;

							}

							.header-area {

								display: flex;
								align-items: center;
								margin: 0 10px 10px;

								svg, .svg {

									margin-right: 10px;
									fill: #fff;
									width: 30px;
									height: auto;
									flex-shrink: 0;

								}

								.h-wrap {

									margin-right: 10px;
									flex-grow: 1;

								}

							}

							.project-type-title, p {

								color: #fff;;

							}

							.project-type-title {

								line-height: 1.1em;

							}

							p {

								color: @darkContrast;
								background-color: #fff;
								padding: 10px;
								flex-grow: 1;
								border-radius: 4px;
								font-size: 14px;
								margin: 0 10px;

								&.error {
									background: none;
									padding: 0;
									margin: 8px 0 0;
									text-align: right;
									position: absolute;
									width: 100%;
									bottom: 0;
									right: 0;
									span {
										background-color: @red;
										color: #fff;
										padding: 4px;
										margin: 0 !important;
										padding: 2px 6px;
										border-radius: 4px 0 0 0;
										display: inline-block;
										max-width: 80%;
									}
								}

								.video-link {
									cursor: pointer;
									color: @formDefaultColour;
									display: inline-block;
									position: relative;
									z-index: 2;
									&:hover {
										text-decoration: underline;
									}
								}

							}

							.overlay {

								&:hover {

									background-color: rgba(0,0,0,0.1);

								}

							}

						}

					}

					.location-info {

						.creating-multiple {

							background-color: @darkContrast;
							padding: 5px 10px;
							color: #fff;
							border-radius: 5px;

							span {

								color: #fff !important;

							}

						}

						.location-name {

				

						}

					}

				}

			}

		}

	}
	
}

@media screen and (min-width: 460px) {

	.modal > .modal-wrap > .modal-inner > .modal-inner-scroll form .project-types > .project-type > .added-to-project{

		position: absolute;
		right: 10px;
		top: 10px;

	}

}

@media screen and (min-width: 768px) {

	.project-created {
		display: flex;
	}

	.content-keywords-wrap {

		&.split {

			display: flex;

			> div {

				flex-grow: 0;
				flex-shrink: 0;
				width: 50%;

				.main-heading {

					margin-top: 0 !important;

				}

				&:first-child {

					padding-right: 30px;

				}

			}

		}

	}

	.modal {

		&.data-dive-limit-reached {

			> .modal-wrap {

				> .modal-inner {

					max-width: 500px;

					> .modal-inner-scroll {

						max-width: 500px;

					}

				}

			}

		}

		> .modal-wrap {

			> .modal-inner {

				max-width: 1300px;

				> .modal-inner-scroll {

					max-width: 1300px;

					form {

						&.small-form {

							max-width: 500px;
        					margin: 0 auto;

							.main-heading {
								margin-top: 30px;
								margin-bottom: 10px;
							}

						}

						.input-2-row {

							display: flex;
							align-items: flex-start;
							margin-bottom: 25px;
							width: 100%;

							.setup-input-wrap {

								flex-grow: 0;
								margin-right: 20px;
								margin-bottom: 0;
								width: 49%;

								&:last-child {

									margin-right: 0;

								}

							}

						}

						.input-1-row, .input-2-row {

							.setup-input-wrap {

								.url-error {

									margin-top: 10px;
									color: @red;

								}

							}

						}
						
						.project-types {

							display: flex;

							.project-type {

								width: 32%;
								margin: 0;

								&:nth-child(2) {

									margin: 0 2%;

								}

								.header-area {

									svg, .svg {

										width: 26px;

									}

								}

							}

							.project-type2 {

								display: flex;
								align-items: center;
								padding-left: 10px;
								padding: 8px;

								&.not-available {
									
									p {

										margin-top: 5px;

									}

								}

								.h-wrap {

									width: 230px;

								}

								.header-area {

									margin-bottom: 0;

								}

							}

						}

					}

				}

			}

		}

	}

}

@media screen and (min-width: 1000px) {

	.keyword-adder-wrap {

		display: flex;

	}

	.keyword-adder-types {

		width: 250px;
		flex-grow: 0;
		flex-shrink: 0;

	}

	.keyword-adder-inner-wrap {

		width: auto;
		flex-grow: 1;
		flex-shrink: 1;
		display: flex;
		gap: 40px;

		&.upload {

			display: block;

		}

		.keyword-suggestions {
			
			
		}
	}

	.select-wraps {

		&.split-dt {

			display: flex;

			.select-wrap {

				flex: 1;
				margin: 0 10px 0 0;

				&:last-child {

					margin: 0;

				}

			}

		}

	}

	.tracking-types {

		display: flex;

		.location-info {

			> hr {

				display: none;

			}

			.select-wraps {

				display: flex;

				.select-wrap {

					flex: 1;
					margin: 0 10px 0 0;

					&:last-child {

						margin: 0;

					}

				}

			}

		}

		.search-device-types {

			padding-right: 50px;
			flex-shrink: 0;
			width: 475px;
			position: relative;

			&:after {

				content: '';
				position: absolute;
				top: 0;
				right: 28px;
				width: 1px;
				height: 100%;
				background-color: fade( @darkContrast, 50% );

			}

		}

		.location-info {

			flex-grow: 1;
			flex-grow: 1;

		}

	}

	.keyword-adder-wrap {
		
		display: flex;

		.keyword-suggestions {

			margin: 0;
			max-width: 400px;

			.keyword-suggestions-list {

				overflow-y: auto;
    			max-height: calc(100vh - 430px);
				max-height: calc(100svh - 430px);
				min-height: 250px;

			}
		}

	}

}

@media screen and (min-width: 1000px) and (min-height: 720px) {

	.keyword-adder-wrap {

		.keyword-suggestions {

			.keyword-suggestions-list {

				max-height: calc(100vh - 460px);
				max-height: calc(100svh - 460px);

			}
		}

	}

}

@media screen and (min-width: 1000px) and (min-height: 1000px) {

	.keyword-adder-wrap {

		.keyword-suggestions {

			.keyword-suggestions-list {

				max-height: calc(100vh - 510px);
				max-height: calc(100svh - 510px);

			}
		}

	}

}

@media screen and (min-width: 1000px) and (min-height: 1200px) {

	.keyword-adder-wrap {

		.keyword-suggestions {

			.keyword-suggestions-list {

				max-height: calc(100vh - 530px);
				max-height: calc(100svh - 530px);

			}
		}

	}

}

@media screen and (min-width: 1300px) {

	.tracking-types {

		.search-device-types {

			width: 570px;

		}

	}

}
</style>